import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { AnalysisModel, ETFDashboardRow, EquityDashboardRow, ScreeningDisposition } from "@features/investments/api";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import { useInvestmentsAccessQuery, useInvestmentsDashboardQuery } from "@features/investments/queries";
import {
  MetricCard,
  PAGE_SIZE_OPTIONS,
  type ResearchRateFilter,
  type ResearchSort,
  type RowsPerPage,
  buildEtfSummary,
  filterEtfRows,
  filterResearchRows,
  formatBasisPointsNullable,
  formatCountShare,
  formatCurrencyNullable,
  formatPercentNullable,
  formatPercentShare,
  formatScoreValue,
  paginate,
  pill,
  worstRatePressureFamilies,
} from "@features/investments/screens/workspace-shared";
import "@views/investments/index.scss";
import { equityRoute, etfRoute } from "@features/investments/routes";

type CoverageAssetType = "equities" | "etfs";

type ConfidenceFilter = "" | "HIGH" | "MEDIUM" | "LOW";
type EvidenceFilter = "" | "stale" | "unsupported" | "low-coverage" | "legacy";

const DISPOSITION_FILTER_OPTIONS: Array<ScreeningDisposition | ""> = ["", "CANDIDATE", "RESEARCH_REQUIRED", "WATCHLIST", "AVOID", "UNAVAILABLE"];
const CONFIDENCE_FILTER_OPTIONS: ConfidenceFilter[] = ["", "HIGH", "MEDIUM", "LOW"];
const RATE_FILTER_OPTIONS: Array<{ value: ResearchRateFilter; label: string; }> = [
  { value: "all", label: "All" },
  { value: "beats3m", label: "Beats 3M" },
  { value: "below3m", label: "Below 3M" },
  { value: "beats10y", label: "Beats 10Y" },
  { value: "below10y", label: "Below 10Y" },
];
const RESEARCH_SORT_OPTIONS: Array<{ value: ResearchSort; label: string; }> = [
  { value: "default", label: "Action then Score" },
  { value: "fcfSpread10yDesc", label: "Best FCF-10Y" },
  { value: "fcfSpread10yAsc", label: "Worst FCF-10Y" },
  { value: "buySpread10yDesc", label: "Best Buy Yld-10Y" },
  { value: "buySpread10yAsc", label: "Worst Buy Yld-10Y" },
];

const CoverageScreen = ({ initialAssetType = "equities" }: { initialAssetType?: CoverageAssetType; }): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const accessQuery = useInvestmentsAccessQuery();
  const dashboardQuery = useInvestmentsDashboardQuery();
  const [assetType, setAssetType] = React.useState<CoverageAssetType>(initialAssetType);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [familyFilter, setFamilyFilter] = React.useState("");
  const [dispositionFilter, setDispositionFilter] = React.useState<ScreeningDisposition | "">(() => parseDispositionFilter(searchParams.get("disposition")));
  const [confidenceFilter, setConfidenceFilter] = React.useState<ConfidenceFilter>(() => parseConfidenceFilter(searchParams.get("confidence")));
  const [modelFilter, setModelFilter] = React.useState<AnalysisModel | "legacy" | "">(() => (searchParams.get("model") || "") as AnalysisModel | "legacy" | "");
  const [evidenceFilter, setEvidenceFilter] = React.useState<EvidenceFilter>(() => parseEvidenceFilter(searchParams.get("evidence")));
  const [rateFilter, setRateFilter] = React.useState<ResearchRateFilter>("all");
  const [sort, setSort] = React.useState<ResearchSort>("default");
  const [pageSize, setPageSize] = React.useState<RowsPerPage>(10);
  const [page, setPage] = React.useState(1);

  const loading = accessQuery.isLoading || dashboardQuery.isLoading;
  const dashboard = dashboardQuery.data;
  const ratesSummary = dashboard?.equityRatesSummary ?? null;
  const pressuredFamilies = React.useMemo(() => worstRatePressureFamilies(ratesSummary, 3), [ratesSummary]);
  const equityRows = React.useMemo(() => {
    const baseRows = filterResearchRows(dashboard?.equityRows ?? [], {
      familyFilter,
      actionFilter: "",
      valuationFilter: "",
      moatFilter: "",
      warningsFilter: "all",
      rateFilter,
      sort,
      searchTerm,
    });
    return sortCoverageRows(baseRows.filter((row) => {
      if (dispositionFilter && row.screeningDisposition !== dispositionFilter) return false;
      if (confidenceFilter && row.dataConfidence !== confidenceFilter) return false;
      if (modelFilter === "legacy" && row.screeningDisposition) return false;
      if (modelFilter && modelFilter !== "legacy" && row.analysisModel !== modelFilter) return false;
      if (evidenceFilter === "stale" && row.researchStale !== true) return false;
      if (evidenceFilter === "unsupported" && row.analysisModelSupported !== false) return false;
      if (evidenceFilter === "low-coverage" && (row.evidenceCoveragePercent ?? 100) >= 75) return false;
      if (evidenceFilter === "legacy" && row.screeningDisposition) return false;
      return true;
    }), sort);
  }, [confidenceFilter, dashboard?.equityRows, dispositionFilter, evidenceFilter, familyFilter, modelFilter, rateFilter, searchTerm, sort]);
  const etfRows = React.useMemo(() => filterEtfRows(dashboard?.etfRows ?? [], searchTerm), [dashboard?.etfRows, searchTerm]);
  const etfSummary = React.useMemo(() => buildEtfSummary(etfRows), [etfRows]);
  const visibleRowCount = assetType === "equities" ? equityRows.length : etfRows.length;
  const totalPages = Math.max(1, Math.ceil(visibleRowCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedEquityRows = React.useMemo(() => paginate(equityRows, currentPage, pageSize), [equityRows, currentPage, pageSize]);
  const pagedEtfRows = React.useMemo(() => paginate(etfRows, currentPage, pageSize), [etfRows, currentPage, pageSize]);

  React.useEffect(() => {
    setPage(1);
  }, [assetType, familyFilter, dispositionFilter, confidenceFilter, modelFilter, evidenceFilter, rateFilter, sort, searchTerm, pageSize]);

  React.useEffect(() => {
    const next = new URLSearchParams(searchParams);
    setOptionalSearchParam(next, "disposition", dispositionFilter);
    setOptionalSearchParam(next, "confidence", confidenceFilter);
    setOptionalSearchParam(next, "model", modelFilter);
    setOptionalSearchParam(next, "evidence", evidenceFilter);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [confidenceFilter, dispositionFilter, evidenceFilter, modelFilter, searchParams, setSearchParams]);

  if (loading) {
    return <InvestmentsLoadingScreen message="Loading coverage workspace..." />;
  }

  if (accessQuery.error) {
    return <InvestmentsPageState message={`Failed to resolve investments access: ${accessQuery.error.message}`} />;
  }

  if (dashboardQuery.error) {
    return <InvestmentsPageState message={`Failed to load coverage workspace: ${dashboardQuery.error.message}`} />;
  }

  return (
    <InvestmentsWorkspaceShell
      title="Coverage"
      description="Snapshot Research keeps cached equities, ETFs, and macro context in one research workspace."
      generatedAt={dashboard?.generatedAt}
    >
      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>Coverage Workspace</h2>
            <p>Switch between equities and ETFs without leaving the research surface. Snapshot data stays precomputed for every authorized user.</p>
          </div>
          <div className="workflow-section-meta">{assetType === "equities" ? `${equityRows.length} equities` : `${etfRows.length} ETFs`}</div>
        </div>

        <section className="investments-card workflow-card coverage-intro-card">
          <div className="coverage-intro-copy">
            <div className="section-eyebrow">Snapshot Research</div>
            <h3>Cached analysis first, operator tools only when needed</h3>
            <p>
              Coverage is read-optimized. Use it to discover and compare names, then drill into detail pages. Live quotes and refresh stay in Operator Mode only.
            </p>
          </div>
          <div className="coverage-mode-switch" role="tablist" aria-label="Coverage asset type">
            <button
              type="button"
              role="tab"
              aria-selected={assetType === "equities"}
              className={assetType === "equities" ? "active" : ""}
              onClick={() => setAssetType("equities")}
            >
              Equities
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={assetType === "etfs"}
              className={assetType === "etfs" ? "active" : ""}
              onClick={() => setAssetType("etfs")}
            >
              ETFs
            </button>
          </div>
        </section>

        {assetType === "equities" ? (
          <>
            {ratesSummary && (
              <section className="investments-card workflow-card macro-banner">
                <div className="macro-banner-copy">
                  <div className="workflow-metric-label">Macro Context</div>
                  <h3>How much of the equity universe still clears Treasury hurdles?</h3>
                  <p>
                    Current benchmarks as of {ratesSummary.asOfDate || "N/A"}: 3M bill {formatPercentNullable(ratesSummary.threeMonthBill)}, 10Y note {formatPercentNullable(ratesSummary.tenYearNote)}.
                    {" "}
                    Keep rates in the research loop instead of treating them as a separate reference page.
                  </p>
                  <div className="macro-banner-copy-actions">
                    <Link className="workflow-header-controls-button" to="/investments/macro">
                      Open Macro
                    </Link>
                  </div>
                </div>
                <div className="macro-banner-metrics">
                  <div className="macro-banner-metric">
                    <div className="macro-banner-metric-label"><GlossaryTerm label="Beats 3M" variant="metric" /></div>
                    <strong className="macro-banner-metric-value">
                      {formatCountShare(ratesSummary.beatingThreeMonthCount, ratesSummary.validCurrentFcfYieldCount)}
                      <span className="macro-banner-metric-subvalue">{formatPercentShare(ratesSummary.beatingThreeMonthCount, ratesSummary.validCurrentFcfYieldCount)}</span>
                    </strong>
                  </div>
                  <div className="macro-banner-metric">
                    <div className="macro-banner-metric-label"><GlossaryTerm label="Beats 10Y" variant="metric" /></div>
                    <strong className="macro-banner-metric-value">
                      {formatCountShare(ratesSummary.beatingTenYearCount, ratesSummary.validCurrentFcfYieldCount)}
                      <span className="macro-banner-metric-subvalue">{formatPercentShare(ratesSummary.beatingTenYearCount, ratesSummary.validCurrentFcfYieldCount)}</span>
                    </strong>
                  </div>
                  <div className="macro-banner-metric">
                    <div className="macro-banner-metric-label"><GlossaryTerm label="Median FCF-10Y" variant="metric" /></div>
                    <strong className="macro-banner-metric-value">{formatBasisPointsNullable(ratesSummary.medianCurrentSpreadToTenYear)}</strong>
                  </div>
                  <div className="macro-banner-metric macro-banner-metric-wide">
                    <div className="macro-banner-metric-label"><GlossaryTerm label="Most Pressured Families" variant="metric" /></div>
                    <div className="macro-banner-family-list">
                      {pressuredFamilies.length === 0 && <span className="macro-banner-family-item">No family pressure data yet.</span>}
                      {pressuredFamilies.map((family) => (
                        <span key={family.key} className="macro-banner-family-item">
                          {family.name}: {formatBasisPointsNullable(family.medianCurrentSpreadToTenYear)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="investments-card workflow-card">
              <div className="workflow-filter-bar coverage-filter-bar">
                <label>
                  <span>Search</span>
                  <input type="text" value={searchTerm} placeholder="Ticker or company" onChange={(event) => setSearchTerm(event.target.value)} />
                </label>
                <label>
                  <span>Family</span>
                  <select value={familyFilter} onChange={(event) => setFamilyFilter(event.target.value)}>
                    <option value="">All families</option>
                    {(dashboard?.equityFamilies ?? []).map((family) => (
                      <option key={family.key} value={family.key}>{family.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Disposition</span>
                  <select value={dispositionFilter} onChange={(event) => setDispositionFilter(event.target.value as ScreeningDisposition | "")}>
                    <option value="">All dispositions</option>
                    {DISPOSITION_FILTER_OPTIONS.filter(Boolean).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Confidence</span>
                  <select value={confidenceFilter} onChange={(event) => setConfidenceFilter(event.target.value as ConfidenceFilter)}>
                    <option value="">All confidence</option>
                    {CONFIDENCE_FILTER_OPTIONS.filter(Boolean).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Model</span>
                  <select value={modelFilter} onChange={(event) => setModelFilter(event.target.value as AnalysisModel | "legacy" | "")}>
                    <option value="">All models</option>
                    <option value="general-equity">General equity</option>
                    <option value="biopharma">Biopharma</option>
                    <option value="financial">Financial</option>
                    <option value="reit">REIT</option>
                    <option value="utility">Utility</option>
                    <option value="legacy">Legacy V1</option>
                  </select>
                </label>
                <label>
                  <span>Evidence</span>
                  <select value={evidenceFilter} onChange={(event) => setEvidenceFilter(event.target.value as EvidenceFilter)}>
                    <option value="">All evidence states</option>
                    <option value="stale">Stale research</option>
                    <option value="unsupported">Unsupported model</option>
                    <option value="low-coverage">Coverage below 75%</option>
                    <option value="legacy">Legacy V1</option>
                  </select>
                </label>
                <label>
                  <span>Rates</span>
                  <select value={rateFilter} onChange={(event) => setRateFilter(event.target.value as ResearchRateFilter)}>
                    {RATE_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Sort</span>
                  <select value={sort} onChange={(event) => setSort(event.target.value as ResearchSort)}>
                    {RESEARCH_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="workflow-table-wrap">
                <table className="investments-table workflow-table">
                  <thead>
                    <tr>
                      <th><GlossaryTerm label="Ticker" variant="table-header" /></th>
                      <th><GlossaryTerm label="Company" variant="table-header" /></th>
                      <th>Disposition</th>
                      <th>Conservative Score</th>
                      <th>Coverage</th>
                      <th>Confidence</th>
                      <th>Model</th>
                      <th><GlossaryTerm label="Current EV / FCF" variant="table-header" /></th>
                      <th><GlossaryTerm label="FCF-10Y" variant="table-header" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedEquityRows.map((row) => (
                      <tr key={row.ticker}>
                        <td><Link to={equityRoute(row.ticker)}>{row.ticker}</Link></td>
                        <td>{row.companyName}</td>
                        <td>{pill(row.screeningDisposition ? row.screeningDisposition.replace(/_/g, " ") : `Legacy ${row.finalAction}`)}</td>
                        <td>{formatScoreValue(row.screeningDisposition ? (row.conservativeScore ?? row.overallScore) : row.overallScore)}</td>
                        <td>{row.screeningDisposition ? `${(row.evidenceCoveragePercent ?? 0).toFixed(0)}%` : "Legacy V1"}</td>
                        <td>{pill(row.dataConfidence)}</td>
                        <td>{coverageModelLabel(row)}</td>
                        <td>{typeof row.evFcfTtm === "number" ? `${row.evFcfTtm.toFixed(1)}x` : "N/A"}</td>
                        <td>{formatBasisPointsNullable(row.ratesBaseline?.currentSpreadToTenYear ?? null)}</td>
                      </tr>
                    ))}
                    {pagedEquityRows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="workflow-empty-cell">No equities match the current coverage filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="workflow-table-footer">
                <div className="workflow-footer-summary">
                  Showing{" "}
                  {equityRows.length === 0
                    ? "0-0"
                    : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, equityRows.length)}`}{" "}
                  of {equityRows.length}
                </div>
                <div className="workflow-footer-controls">
                  <label className="workflow-footer-rows">
                    <span>Rows</span>
                    <select
                      className="workflow-footer-select"
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value) as RowsPerPage)}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="workflow-footer-button"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    Previous
                  </button>
                  <span className="workflow-footer-page">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="workflow-footer-button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <div className="workflow-grid workflow-grid-etf">
              <MetricCard label="ETF Count" value={String(etfRows.length)} />
              <MetricCard label="Asset Classes" value={String(etfSummary.assetClasses.length)} />
              <MetricCard label="Median Expense Ratio" value={formatPercentNullable(etfSummary.medianExpenseRatio)} />
              <MetricCard
                label="Largest AUM"
                value={etfSummary.largestAum ? `${etfSummary.largestAum.ticker} · ${formatCurrencyNullable(etfSummary.largestAum.etf?.assetsUnderManagement ?? null)}` : "N/A"}
              />
            </div>

            <section className="investments-card workflow-card">
              <div className="workflow-filter-bar workflow-filter-bar-compact">
                <label>
                  <span>Search</span>
                  <input type="text" value={searchTerm} placeholder="Ticker or fund" onChange={(event) => setSearchTerm(event.target.value)} />
                </label>
              </div>
              <div className="workflow-table-wrap">
                <table className="investments-table workflow-table">
                  <thead>
                    <tr>
                      <th><GlossaryTerm label="Ticker" variant="table-header" /></th>
                      <th><GlossaryTerm label="Fund" variant="table-header" /></th>
                      <th><GlossaryTerm label="Asset Class" variant="table-header" /></th>
                      <th><GlossaryTerm label="Expense Ratio" variant="table-header" /></th>
                      <th><GlossaryTerm label="AUM" variant="table-header" /></th>
                      <th><GlossaryTerm label="Domicile" variant="table-header" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedEtfRows.map((row) => (
                      <CoverageEtfRow key={row.ticker} row={row} />
                    ))}
                    {pagedEtfRows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="workflow-empty-cell">No ETFs match the current search.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="workflow-table-footer">
                <div className="workflow-footer-summary">
                  Showing{" "}
                  {etfRows.length === 0
                    ? "0-0"
                    : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, etfRows.length)}`}{" "}
                  of {etfRows.length}
                </div>
                <div className="workflow-footer-controls">
                  <label className="workflow-footer-rows">
                    <span>Rows</span>
                    <select
                      className="workflow-footer-select"
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value) as RowsPerPage)}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="workflow-footer-button"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    Previous
                  </button>
                  <span className="workflow-footer-page">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="workflow-footer-button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </section>
    </InvestmentsWorkspaceShell>
  );
};

function CoverageEtfRow({ row }: { row: ETFDashboardRow; }): JSX.Element {
  return (
    <tr>
      <td><Link to={etfRoute(row.ticker)}>{row.ticker}</Link></td>
      <td>{row.companyName}</td>
      <td>{row.etf?.assetClass || "N/A"}</td>
      <td>{formatPercentNullable(row.etf?.expenseRatio ?? null)}</td>
      <td>{formatCurrencyNullable(row.etf?.assetsUnderManagement ?? null)}</td>
      <td>{row.etf?.domicile || "N/A"}</td>
    </tr>
  );
}

export default CoverageScreen;

function coverageModelLabel(row: EquityDashboardRow): string {
  if (!row.screeningDisposition) return "Legacy V1";
  const model = (row.analysisModel || "unclassified").replace(/-/g, " ");
  if (row.analysisModelSupported === false) return `${model} · unsupported`;
  if (row.researchStale) return `${model} · stale research`;
  return model;
}

function sortCoverageRows(rows: EquityDashboardRow[], sort: ResearchSort): EquityDashboardRow[] {
  if (sort !== "default") return rows;
  const rank: Record<string, number> = { CANDIDATE: 0, RESEARCH_REQUIRED: 1, WATCHLIST: 2, AVOID: 3, UNAVAILABLE: 4 };
  return [...rows].sort((left, right) => {
    const leftRank = left.screeningDisposition ? (rank[left.screeningDisposition] ?? 5) : 6;
    const rightRank = right.screeningDisposition ? (rank[right.screeningDisposition] ?? 5) : 6;
    if (leftRank !== rightRank) return leftRank - rightRank;
    const scoreDelta = (right.conservativeScore ?? right.overallScore) - (left.conservativeScore ?? left.overallScore);
    return scoreDelta !== 0 ? scoreDelta : left.ticker.localeCompare(right.ticker);
  });
}

function parseDispositionFilter(value: string | null): ScreeningDisposition | "" {
  return DISPOSITION_FILTER_OPTIONS.includes(value as ScreeningDisposition) ? value as ScreeningDisposition : "";
}

function parseConfidenceFilter(value: string | null): ConfidenceFilter {
  return CONFIDENCE_FILTER_OPTIONS.includes(value as ConfidenceFilter) ? value as ConfidenceFilter : "";
}

function parseEvidenceFilter(value: string | null): EvidenceFilter {
  return ["stale", "unsupported", "low-coverage", "legacy"].includes(value || "") ? value as EvidenceFilter : "";
}

function setOptionalSearchParam(params: URLSearchParams, key: string, value: string): void {
  if (value) params.set(key, value);
  else params.delete(key);
}
